import React from 'react';
import { SafetyAction, ActionStatus, ActionType, ActionPriority } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface ActionsTableProps {
  actions: SafetyAction[];
  loading: boolean;
  onSelect: (action: SafetyAction) => void;
  onRefresh: () => void;
}

export const ActionsTable: React.FC<ActionsTableProps> = ({
  actions,
  loading,
  onSelect,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(actions);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: ActionStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'ASSIGNED':
        return <span className="badge-pill badge-info">Assigned</span>;
      case 'ACCEPTED':
        return <span className="badge-pill badge-info">Accepted</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-warning">In Progress</span>;
      case 'COMPLETED':
        return <span className="badge-pill badge-info">Completed</span>;
      case 'PENDING_VERIFICATION':
        return <span className="badge-pill badge-warning">Pending Verification</span>;
      case 'VERIFIED':
        return <span className="badge-pill badge-success">Verified</span>;
      case 'EFFECTIVENESS_REVIEW':
        return <span className="badge-pill badge-info">Effectiveness Review</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">Cancelled</span>;
      default:
        return <span className="badge-pill badge-neutral">{String(status).replace(/_/g, ' ')}</span>;
    }
  };

  const getTypeBadge = (type: ActionType) => {
    switch (type) {
      case 'CORRECTIVE':
        return <span className="badge-pill badge-danger">Corrective</span>;
      case 'PREVENTIVE':
        return <span className="badge-pill badge-info">Preventive</span>;
      case 'CONTAINMENT':
        return <span className="badge-pill badge-warning">Containment</span>;
      default:
        return <span className="badge-pill badge-neutral">{type}</span>;
    }
  };

  const getPriorityBadge = (priority: ActionPriority) => {
    switch (priority) {
      case 'IMMEDIATE':
        return <span className="badge-pill badge-danger">Immediate</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      case 'LOW':
        return <span className="badge-pill badge-neutral">Low</span>;
      default:
        return <span className="badge-pill badge-neutral">{priority}</span>;
    }
  };

  const isOverdue = (dueDateStr: string, status: ActionStatus) => {
    if (status === 'CLOSED' || status === 'CANCELLED') return false;
    const due = new Date(dueDateStr);
    const now = new Date();
    due.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return due < now;
  };

  const getDaysOverdue = (dueDateStr: string) => {
    const due = new Date(dueDateStr);
    const now = new Date();
    due.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diff = now.getTime() - due.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  if (!loading && actions.length === 0) {
    return (
      <div className="card" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
        No safety actions found matching filter criteria.
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="actionNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Action Ref
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Action Title & Context
              </SortableTh>
              <SortableTh field="actionType" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Type & Priority
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <SortableTh field="assigneeName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Assignee / Dept
              </SortableTh>
              <SortableTh field="currentDueDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Target Due Date
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((a) => {
              const overdue = isOverdue(a.currentDueDate, a.status);
              const days = overdue ? getDaysOverdue(a.currentDueDate) : 0;
              return (
                <tr
                  key={a.id}
                  onClick={() => onSelect(a)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 700, color: '#0284c7', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {a.actionNumber}
                  </td>
                  <td style={{ maxWidth: '340px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>{a.title}</div>
                    {a.sourceReference && (
                      <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: 500 }}>
                        Source: {a.sourceModule ? `[${a.sourceModule}] ` : ''}{a.sourceReference}
                      </div>
                    )}
                    {a.description && !a.sourceReference && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#64748b',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {a.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {getTypeBadge(a.actionType)}
                      {getPriorityBadge(a.priority)}
                    </div>
                  </td>
                  <td>{getStatusBadge(a.status)}</td>
                  <td style={{ fontSize: '12px', color: '#334155' }}>
                    <div style={{ fontWeight: 500 }}>{a.assigneeName || 'Unassigned'}</div>
                    <div style={{ color: '#64748b', fontSize: '11px' }}>{a.department || 'General'}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          fontSize: '12px',
                          color: overdue ? '#dc2626' : '#475569',
                          fontWeight: overdue ? 700 : 400,
                        }}
                      >
                        {new Date(a.currentDueDate).toLocaleDateString()}
                      </span>
                      {overdue && (
                        <span className="badge-pill badge-danger" style={{ fontSize: '10px' }}>
                          +{days}d Overdue
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(a);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 10px', fontSize: '12px' }}
                    >
                      Dossier ↗
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="safety actions"
      />
    </div>
  );
};
